// components/PeriodFilter.tsx
import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { SurveyContext, SurveyStatus } from '../types';
import { useSurveyResultFiltersLoader } from './SurveyResultFiltersLoader';

interface PeriodFilterProps {
  onSubmit: ({
    period,
    surveyContext,
    surveyStatus,
    title,
  }: {
    period?: string;
    surveyContext?: SurveyContext;
    surveyStatus?: SurveyStatus;
    title?: string;
  }) => void;
}

interface SearchFiltersType {
  title: string | undefined;
  period: OutlinedSelectOptionType | null;
  surveyContext: OutlinedSelectOptionType | null;
  surveyStatus: OutlinedSelectOptionType | null;
}

function SurveyResultFilters({ onSubmit }: PeriodFilterProps) {
  const { data } = useSurveyResultFiltersLoader();
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_SURVEY_RESULTS,
  );
  const { control, handleSubmit, reset } = useForm<SearchFiltersType>();
  const localSubmit = (values: SearchFiltersType) => {
    onSubmit({
      period: values.period?.value as string | undefined,
      surveyContext: values.surveyContext?.value as SurveyContext | undefined,
      surveyStatus: values.surveyStatus?.value as SurveyStatus | undefined,
      title: values.title?.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit(localSubmit)}>
      <Row>
        <Col xs={12} md={4}>
          <TextOutlinedInput
            control={control}
            name="title"
            label={t('filters.title')}
            placeholder={t('filters.titlePlaceholder')}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="surveyContext"
            label={t('filters.surveyContext')}
            placeholder={t('filters.surveyContextPlaceholder')}
            options={(data?.surveyContexts ?? []).map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="period"
            label={t('filters.period')}
            placeholder={t('filters.periodPlaceholder')}
            options={(data?.periods ?? []).map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <OutlinedSelect
            control={control}
            name="surveyStatus"
            label={t('filters.surveyStatus')}
            placeholder={t('filters.surveyStatusPlaceholder')}
            options={(data?.surveyStatuses ?? []).map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        </Col>
        <Col xs={12} md={8}>
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="primary"
              className="mb-3"
              size="md"
              text={t('filters.search')}
            />
            <Button
              type="submit"
              color="primary"
              className="mb-3 ml-2"
              onClick={() => {
                reset({
                  surveyStatus: null,
                  period: null,
                  surveyContext: null,
                  title: '',
                });
              }}
              size="md"
              text={t('filters.clean')}
            />
          </div>
        </Col>
      </Row>
    </form>
  );
}

export default SurveyResultFilters;
